import { AxiosError, AxiosRequestConfig, AxiosResponse, AxiosInstance } from '../Axios';
import { CollectionType, PreferenceType, ErrorType, getIdentity } from '../../internal';

const URL_REGEX_UID_PART = '@currentUser';
const URL_REGEX_END_PART = '($|(/|\\?).*)';
const SERVICE_ENDPT = '/preferences/preferences/';
export const SERVICE_URL = `${SERVICE_ENDPT}@currentUser`;
const SERVICE_URL_REGEXP = new RegExp(
  `^${SERVICE_ENDPT}${URL_REGEX_UID_PART}${URL_REGEX_END_PART}`
);
// max java integer
const MAX_ITEMS_LIMIT = Math.pow(2, 31) - 1;
const ERRONEOUS_PREF_ID = '_hc_xx_';
const MAX_INIT_ATTEMPTS = 5;

type JqXhrSuccessCallback = (data: any, textStatus: string, jqXhr: IJqXhr) => any;
type JqXhrErrCallback = (jqXhr: IJqXhr, textStatus: string, errorThrown: string) => any;
type JqXhrAlwaysCallback = (a1: any, textStatus: string, a3: ErrorObject) => any;
type ErrorObject = IJqXhr | string;

// IJqXhr is a contract compatible with openui-framework.
// https://api.jquery.com/jQuery.ajax/
export interface IJqXhr {
  always: (cb: JqXhrAlwaysCallback) => this;
  done: (cb: JqXhrSuccessCallback) => this;
  fail: (cb: JqXhrErrCallback) => this;
  getAllResponseHeaders: () => any;
  getResponseHeader(name: string): string;
  // promise: () => this; Should not be used.
  readyState: number; // 4
  responseJSON: any;
  responseText: string;
  setRequestHeader: () => this;
  //   state: ƒ ()
  status: string;
  // jquery seems to return the instance itself on calling status code.
  //  no consumers should be using it if they use promise apis.
  statusCode: () => this;
  statusText: string; // "OK"
  then: (s: JqXhrSuccessCallback, f?: JqXhrErrCallback) => this;
}

// ICompatiblePrefCacheHelper represents cache helper contract compatible with
// older version of cache in openui-framework.
export interface ICompatiblePrefCacheHelper {
  instanceUrlRegEx: RegExp;
  cachedData?: CollectionType<PreferenceType>;
  textStatus: string;
  cachedJqXhr?: IJqXhr;
  cachedErrObjects?: Array<ErrorObject>;
  alternateServiceUrl: string;

  initialized(): boolean;
  init(): void;
  extractPrefId(s: string): string | undefined;
  addToCache<T>(data: T, s: string, ghr: IJqXhr): void;
  removeFromCache(o: { url?: string; data?: any }): void;
}

// IPrefCacheHelper represents reloadable cache helper contract.
export interface IPrefCacheHelper extends ICompatiblePrefCacheHelper {
  reload(): void;
}

// JqXhrAdapter is an adapter on axios response for compatibility with openui-framework.
export class JqXhrAdapter implements IJqXhr {
  constructor(private axRes: AxiosResponse) {}

  always(cb: JqXhrAlwaysCallback): this {
    if (this.axRes.status < 400) {
      cb.apply(this, [this.responseJSON, this.statusText, this]);
    } else {
      cb.apply(this, [this, this.statusText, this.statusText]);
    }
    return this;
  }

  done(cb: JqXhrSuccessCallback): this {
    cb.apply(this, [this.responseJSON, this.statusText, this]);
    return this;
  }

  fail(cb: JqXhrErrCallback): this {
    cb.apply(this, [this, this.statusText, this.statusText]);
    return this;
  }

  getAllResponseHeaders(): any {
    return this.axRes.headers;
  }

  getResponseHeader(name: string): string {
    return this.axRes.headers[name.toLowerCase()];
  }

  get readyState(): number {
    return this.axRes.request!.readyState;
  }

  get responseJSON(): any {
    return this.axRes.data;
  }

  get responseText(): string {
    return this.axRes.request!.responseText;
  }

  setRequestHeader(): this {
    // no op.
    return this;
  }
  get status(): string {
    return this.axRes.status.toString();
  }
  statusCode(): this {
    return this;
  }

  get statusText(): string {
    if (this.axRes.status < 300) {
      return 'success';
    }
    return this.axRes.statusText;
  }

  then(s: JqXhrSuccessCallback, f?: JqXhrErrCallback): this {
    if (this.axRes.status < 300) {
      s.apply(this, [this.responseJSON, this.statusText, this]);
    } else if (f !== undefined) {
      f.apply(this, [this, this.statusText, this.statusText]);
    }
    return this;
  }
}

// CompatibleError represents error object compatible with that of openui-framework.
export class CompatibleError extends JqXhrAdapter implements AxiosError<ErrorType> {
  private axErr: AxiosError<ErrorType>;
  constructor(axErr: AxiosError<ErrorType>) {
    super(axErr.response!);
    this.axErr = axErr;
  }

  get name(): string {
    return this.axErr.name;
  }

  get message(): string {
    return this.axErr.message;
  }

  get stack(): string | undefined {
    return this.axErr.stack;
  }

  get config(): AxiosRequestConfig {
    return this.axErr.config;
  }
  get response(): AxiosResponse<ErrorType> | undefined {
    return this.axErr.response;
  }
  get code(): string | undefined {
    return this.axErr.code;
  }
  get request(): any | undefined {
    return this.axErr.request;
  }
  get isAxiosError(): boolean {
    return this.axErr.isAxiosError;
  }
  toJSON(): object {
    return this.axErr.toJSON();
  }
}

//
// @class
// @name PrefCacheHelper
// User preference cache helper. Used by cache to deal with data.
// This class is CRITICAL for backward compatibility and can be shared
// across versions of Html Commons. Any changes to this class MUST be
// vetted for regression.
//
// TODO periodic refresh.
//
export class PrefCacheHelper implements IPrefCacheHelper {
  /* private variables */
  private dataInitialized = false;
  private dataInitializing = false;
  private errorDataInitialized = false;
  private errorDataInitializing = false;
  private uid?: string;
  private initAttempts = 0;

  cachedJqXhr?: IJqXhr;
  cachedData?: CollectionType<PreferenceType>;
  cachedErrObjects?: Array<ErrorObject>;
  alternateServiceUrl = SERVICE_URL;
  instanceUrlRegEx: RegExp = SERVICE_URL_REGEXP;

  constructor(private axiosInstance: AxiosInstance) {
    this.init();
  }

  // getData loads preferences data from server.
  private getData(): Promise<AxiosResponse<CollectionType<PreferenceType>>> {
    return this.axiosInstance.get<CollectionType<PreferenceType>>(SERVICE_URL, {
      headers: {
        Accept: 'application/json',
      },
      params: {
        limit: MAX_ITEMS_LIMIT,
      },
      cache: false,
    });
  }

  // init initializes cache.
  async init() {
    if (
      this.dataInitializing ||
      this.errorDataInitializing ||
      this.initAttempts > MAX_INIT_ATTEMPTS
    ) {
      return;
    }
    this.initAttempts++;
    this.dataInitialized = false;
    this.dataInitializing = true;
    try {
      const resp: AxiosResponse<CollectionType<PreferenceType>> = await this.getData();
      this.cachedData = Object.seal(resp.data);
      this.cachedJqXhr = new JqXhrAdapter(resp);

      // load user id
      if (!this.uid) {
        const uIDType = await getIdentity();
        this.uid = uIDType.id;
        if (this.uid) {
          this.instanceUrlRegEx = new RegExp(
            `^${SERVICE_ENDPT}(@currentUser|${this.uid})${URL_REGEX_END_PART}`
          );
          this.alternateServiceUrl = SERVICE_ENDPT + this.uid;
        }
      }
      this.dataInitializing = false;
      this.dataInitialized = true;
    } catch (error) {
      this.dataInitializing = false;
      this.cachedData = undefined;
      this.dataInitialized = false;
    }
    if (!this.cachedErrObjects) {
      this.errorDataInitialized = false;
      this.errorDataInitializing = true;
      try {
        await this.axiosInstance.get<AxiosResponse<PreferenceType>>(
          `${SERVICE_URL}/${ERRONEOUS_PREF_ID}`,
          {
            headers: {
              Accept: 'application/json',
            },
            cache: false,
          }
        );
      } catch (error) {
        const axErr = error as AxiosError<ErrorType>;
        this.cachedErrObjects = [Object.seal(new CompatibleError(axErr)), 'error', 'Not Found'];
      }
      this.errorDataInitialized = true;
      this.errorDataInitializing = false;
    }
  }

  // initialized return true if cache is initialized. False otherwise.
  initialized(): boolean {
    return this.dataInitialized && this.errorDataInitialized;
  }

  // extractPrefId extracts the preference id from input path.
  extractPrefId(path: string): string | undefined {
    if (path.indexOf(SERVICE_URL) === 0) {
      return path.substring(SERVICE_URL.length + 1);
    }
    if (path.indexOf(this.alternateServiceUrl) === 0) {
      return path.substring(this.alternateServiceUrl.length + 1);
    }
  }

  get textStatus(): string {
    if (this.cachedJqXhr) {
      return this.cachedJqXhr.statusText;
    }
    return '';
  }

  // addToCache adds data from input response to cache.
  addToCache<T>(respData: T, _: string, jq: IJqXhr) {
    let contentTypeHdr: string = jq.getResponseHeader('Content-Type');
    if (!contentTypeHdr || contentTypeHdr.indexOf('json') === -1 || !this.dataInitialized) return;
    contentTypeHdr = contentTypeHdr.toLowerCase();
    // check if data is collection.
    if (contentTypeHdr.indexOf('vnd.sas.collection') !== -1) {
      const data = (respData as unknown) as CollectionType<PreferenceType>;
      const newItems = data.items.filter((newItem: PreferenceType) => {
        return this.cachedData!.items.every((item: PreferenceType, i: number) => {
          if (item.id === newItem.id) {
            this.cachedData!.items[i] = newItem;
            return false;
          }
          return true;
        });
      });
      this.cachedData!.items.push(...newItems);
    } else if (contentTypeHdr.indexOf('vnd.sas.preference') !== -1) {
      const data: PreferenceType = (respData as unknown) as PreferenceType;
      const isNew = this.cachedData!.items.every((item: PreferenceType, i: number) => {
        if (item.id === data.id) {
          this.cachedData!.items[i] = data;
          return false;
        }
        return true;
      });
      isNew && this.cachedData!.items.push(data);
    }
    this.cachedData!.count = this.cachedData!.items.length;
  }

  // removeFromCache removes cached data associated with input request.
  removeFromCache(c: AxiosRequestConfig) {
    if (this.dataInitialized === false && !c.url) return;
    const parsedUri = new URL(c.url!, self.location.origin);
    const prefId = this.extractPrefId(parsedUri.pathname);
    const parsedData =
      !prefId && c.data && typeof c.data === 'string' ? JSON.parse(c.data) : c.data;

    if (parsedData && Array.isArray(parsedData.items)) {
      // was a bulk delete
      for (const itm of parsedData.items) {
        for (let ci = this.cachedData!.items.length; ci--; ) {
          if (this.cachedData!.items[ci].id === itm.id) {
            this.cachedData!.items.splice(ci, 1);
            this.cachedData!.count = this.cachedData!.items.length;
            break;
          }
        }
      }
    } else {
      const matchingIndex = this.cachedData!.items.findIndex(
        (item: PreferenceType) => item.id === prefId
      );
      if (matchingIndex !== -1) {
        this.cachedData!.items.splice(matchingIndex, 1);
        this.cachedData!.count = this.cachedData!.items.length;
      }
    }
  }

  async reload() {
    if (!this.initialized()) {
      this.init();
      return;
    }
    // reload data.
    try {
      const resp: AxiosResponse<CollectionType<PreferenceType>> = await this.getData();
      this.cachedData = Object.seal(resp.data);
      this.cachedJqXhr = new JqXhrAdapter(resp);
    } catch (error) {
      // TODO log error.
      // ignore.
    }
  }
}
